import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Fingerprint, MapPin, Printer, ArrowLeft, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  captureFingerprint,
  getCurrentLocation,
  printReceipt,
  getDeviceInfo,
  isNativeAgentApp,
} from '@/lib/agent-bridge';
import { requireStepUp, canonicalSummary } from '@/lib/stepup-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const GRANT_TYPES = [
  { value: 'sassa_old_age',         label: 'SASSA — Old age' },
  { value: 'sassa_disability',      label: 'SASSA — Disability' },
  { value: 'sassa_child',           label: 'SASSA — Child support' },
  { value: 'sassa_war_veteran',     label: 'SASSA — War veteran' },
  { value: 'sassa_foster_care',     label: 'SASSA — Foster care' },
  { value: 'sassa_care_dependency', label: 'SASSA — Care dependency' },
  { value: 'sassa_grant_in_aid',    label: 'SASSA — Grant in aid' },
  { value: 'private',               label: 'Private grant' },
];

/**
 * Phase 5 — Agent Grant Payout flow.
 *
 * Wizard order:
 *   1. Beneficiary details (ID, name, amount, grant type, optional ref)
 *   2. Capture biometric — calls the hardware bridge (native plugin OR
 *      browser polyfill).
 *   3. Lock GPS location — same bridge.
 *   4. Step-up auth — agent presents passkey (or OTP) before payment.
 *   5. Submit to /api/grants/payout — server validates everything and
 *      returns the printable receipt + hash.
 *   6. Print receipt — bridge again, prints on the device's thermal
 *      printer or opens a browser print dialog.
 *
 * Every step is independently retryable and surfaces a clear "what went
 * wrong" message. The agent never sees raw hashes; they see green/red.
 */
const AgentGrantPayoutPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Form
  const [beneficiaryIdNumber, setBeneficiaryIdNumber] = useState('');
  const [beneficiaryFullName, setBeneficiaryFullName] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [grantType, setGrantType] = useState('sassa_old_age');
  const [grantReference, setGrantReference] = useState('');

  // Captures
  const [biometric, setBiometric] = useState(null);   // {hash, confidence, method}
  const [gps, setGps]             = useState(null);   // {latitude, longitude, accuracy_m}
  const [device, setDevice]       = useState(null);   // {device_id, app_version}

  // Submission
  const [busy, setBusy]               = useState(false);
  const [stepUpTicket, setStepUpTicket] = useState(null);
  const [receipt, setReceipt]         = useState(null);

  useEffect(() => { (async () => setDevice(await getDeviceInfo()))(); }, []);

  // Step gating
  const amountZar = Number(amountStr) || 0;
  const canCapture = !!(beneficiaryIdNumber && beneficiaryIdNumber.length === 13
                       && beneficiaryFullName && amountZar > 0 && grantType);

  const handleCaptureFingerprint = async () => {
    setBusy(true);
    try {
      const out = await captureFingerprint();
      setBiometric(out);
      toast.success(`Fingerprint captured (confidence ${(out.confidence * 100).toFixed(0)}%)`);
    } catch (e) {
      toast.error(e?.message || 'Fingerprint capture failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCaptureGps = async () => {
    setBusy(true);
    try {
      const out = await getCurrentLocation();
      setGps(out);
      toast.success(`Location locked (±${Math.round(out.accuracy_m)}m)`);
    } catch (e) {
      toast.error(e?.message || 'Location lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const handleStepUp = async () => {
    setBusy(true);
    try {
      const summaryMeta = {
        beneficiary_id_number: beneficiaryIdNumber,
        beneficiary_full_name: beneficiaryFullName,
        grant_type:            grantType,
      };
      const result = await requireStepUp({
        action:    'grant.payout',
        amountZar: amountZar,
        summary:   summaryMeta,
      });
      if (result.skipped) {
        // grant.payout is on the "always required" list so this shouldn't
        // happen — but if a settings rollback removed it, we still
        // require the agent to acknowledge explicitly.
        toast.error('Step-up unexpectedly skipped — refusing to proceed');
        return;
      }
      setStepUpTicket(result);
      toast.success('Step-up confirmed — submitting payout');
    } catch (e) {
      toast.error(e?.message || 'Step-up failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!biometric || !gps || !stepUpTicket) {
      toast.error('Capture biometric, GPS, and step-up before submitting');
      return;
    }
    setBusy(true);
    try {
      const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `agent-grant-${Date.now()}`;
      const resp = await axios.post(
        `${API}/grants/payout`,
        {
          beneficiary_id_number: beneficiaryIdNumber,
          beneficiary_full_name: beneficiaryFullName,
          amount_zar:            amountZar,
          grant_type:            grantType,
          grant_reference:       grantReference || null,
          biometric:             biometric,
          gps:                   gps,
          device_id:             device?.device_id,
          device_app_version:    device?.app_version,
        },
        {
          headers: {
            'Idempotency-Key':     idempotencyKey,
            'X-Step-Up-Ticket':    stepUpTicket.ticketId,
          },
        },
      );
      setReceipt(resp.data);
      toast.success(`Payout recorded — receipt ${resp.data.receipt_no}`);
      setStep(6);
    } catch (e) {
      const msg = e?.response?.data?.detail?.detail
                || e?.response?.data?.detail
                || e?.message
                || 'Payout failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    if (!receipt?.printed_receipt) return;
    try {
      await printReceipt(receipt.printed_receipt);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B0C34] via-[#2D1854] to-[#1B0C34] p-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/agent/dashboard')}
          className="text-white mb-4"
          data-testid="agent-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-[#1B0C34]">Grant payout</CardTitle>
            <CardDescription>
              Step {step} of 5 — capture beneficiary, biometric, location, then confirm.{' '}
              {isNativeAgentApp() ? (
                <Badge variant="default" className="ml-2">Agent device</Badge>
              ) : (
                <Badge variant="secondary" className="ml-2">Browser preview</Badge>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <>
                <div>
                  <Label htmlFor="ben-id">Beneficiary ID number (13 digits)</Label>
                  <Input
                    id="ben-id"
                    value={beneficiaryIdNumber}
                    inputMode="numeric"
                    maxLength={13}
                    onChange={(e) => setBeneficiaryIdNumber(e.target.value.replace(/[^0-9]/g, ''))}
                    data-testid="grant-ben-id-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ben-name">Beneficiary full name</Label>
                  <Input
                    id="ben-name"
                    value={beneficiaryFullName}
                    onChange={(e) => setBeneficiaryFullName(e.target.value)}
                    data-testid="grant-ben-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Amount (ZAR)</Label>
                  <Input
                    id="amount"
                    type="number"
                    inputMode="decimal"
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                    data-testid="grant-amount-input"
                  />
                </div>
                <div>
                  <Label>Grant type</Label>
                  <Select value={grantType} onValueChange={setGrantType}>
                    <SelectTrigger data-testid="grant-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GRANT_TYPES.map((g) => (
                        <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="grant-ref">Grant reference (optional)</Label>
                  <Input
                    id="grant-ref"
                    value={grantReference}
                    onChange={(e) => setGrantReference(e.target.value)}
                    data-testid="grant-ref-input"
                  />
                </div>
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canCapture}
                  className="w-full"
                  data-testid="grant-step1-next-btn"
                >Continue</Button>
              </>
            )}

            {step === 2 && (
              <div className="space-y-4 text-center">
                <Fingerprint className="w-16 h-16 mx-auto text-[#145539]" />
                <p>Ask {beneficiaryFullName || 'the beneficiary'} to place their finger on the sensor.</p>
                {biometric && (
                  <div className="text-sm text-slate-600">
                    Captured (confidence {(biometric.confidence * 100).toFixed(0)}%)
                  </div>
                )}
                <Button
                  onClick={handleCaptureFingerprint}
                  disabled={busy}
                  data-testid="grant-capture-fp-btn"
                >Capture fingerprint</Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  disabled={!biometric}
                  className="ml-2"
                  data-testid="grant-step2-next-btn"
                >Continue</Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4 text-center">
                <MapPin className="w-16 h-16 mx-auto text-[#145539]" />
                <p>Lock the device's current location for the FICA record.</p>
                {gps && (
                  <div className="text-sm text-slate-600">
                    {gps.latitude.toFixed(5)}, {gps.longitude.toFixed(5)} (±{Math.round(gps.accuracy_m)}m)
                  </div>
                )}
                <Button
                  onClick={handleCaptureGps}
                  disabled={busy}
                  data-testid="grant-capture-gps-btn"
                >Lock location</Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(4)}
                  disabled={!gps}
                  className="ml-2"
                  data-testid="grant-step3-next-btn"
                >Continue</Button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4 text-center">
                <CheckCircle2 className={`w-16 h-16 mx-auto ${stepUpTicket ? 'text-[#145539]' : 'text-amber-500'}`} />
                <p>Confirm the payout with your passkey.</p>
                {stepUpTicket && (
                  <div className="text-sm text-slate-600">
                    Step-up confirmed via {stepUpTicket.authMethod}
                  </div>
                )}
                <Button
                  onClick={handleStepUp}
                  disabled={busy || !!stepUpTicket}
                  data-testid="grant-stepup-btn"
                >{stepUpTicket ? 'Confirmed' : 'Confirm with passkey'}</Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(5)}
                  disabled={!stepUpTicket}
                  className="ml-2"
                  data-testid="grant-step4-next-btn"
                >Review & submit</Button>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-4">
                <p className="font-medium">Review the payment before submitting:</p>
                <pre className="bg-slate-50 p-3 text-xs whitespace-pre-wrap">
{`Beneficiary:    ${beneficiaryFullName} (${beneficiaryIdNumber})
Amount:         ZAR ${amountZar.toFixed(2)}
Grant type:     ${grantType}
Grant ref:      ${grantReference || '-'}
Biometric:      ${biometric?.hash || '-'} (${((biometric?.confidence || 0) * 100).toFixed(0)}%)
GPS:            ${gps?.latitude.toFixed(5)}, ${gps?.longitude.toFixed(5)} (±${Math.round(gps?.accuracy_m || 0)}m)
Device id:      ${device?.device_id || '-'}`}
                </pre>
                <Button
                  onClick={handleSubmit}
                  disabled={busy || !biometric || !gps || !stepUpTicket}
                  className="w-full"
                  data-testid="grant-submit-btn"
                >{busy ? 'Submitting…' : 'Submit payout'}</Button>
              </div>
            )}

            {step === 6 && receipt && (
              <div className="space-y-4">
                <div className="flex items-center justify-center text-[#145539]">
                  <CheckCircle2 className="w-12 h-12 mr-2" />
                  <span className="text-lg font-semibold">Payment recorded</span>
                </div>
                <pre
                  className="bg-slate-50 p-3 text-xs whitespace-pre-wrap border border-slate-200 rounded"
                  data-testid="grant-receipt-pre"
                >{receipt.printed_receipt}</pre>
                <div className="flex gap-2">
                  <Button
                    onClick={handlePrint}
                    className="flex-1"
                    data-testid="grant-print-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/agent/dashboard')}
                    className="flex-1"
                  >Done</Button>
                </div>
                <p className="text-xs text-slate-500">
                  Receipt hash: <code data-testid="grant-receipt-hash">{receipt.printed_receipt_hash}</code>
                </p>
              </div>
            )}

            {step !== 6 && (
              <div className="text-xs text-slate-400 text-center mt-4">
                <AlertTriangle className="inline w-3 h-3 mr-1" />
                You are paying ZAR {amountZar.toFixed(2)} from your agent terminal.
                This action is recorded for FICA.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentGrantPayoutPage;
